<template>
    <button
        :class="[
            'btn-base',
            `btn-${variant}`,
            `btn-size-${size}`,
            {
                'is-loading': loading,
                'is-plain': plain,
                'is-round': round,
                'is-circle': circle,
            },
        ]"
        :disabled="disabled || loading"
        :type="type"
    >
        <svg v-if="loading" class="btn-loading-icon" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" opacity="0.25" />
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" opacity="0.75" />
        </svg>
        <slot />
    </button>
</template>

<script setup lang="ts">
    defineOptions({ name: 's-button' })

    type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'link' | 'warning'
    type Size = 'sm' | 'md' | 'lg' | 'xl' | 'icon'

    interface Props {
        variant?: Variant
        size?: Size
        type?: 'button' | 'submit' | 'reset'
        disabled?: boolean
        loading?: boolean
        /** 朴素按钮（浅色背景 + 主色边框） */
        plain?: boolean
        /** 圆角按钮（大圆角 1rem） */
        round?: boolean
        /** 圆形按钮 */
        circle?: boolean
    }

    withDefaults(defineProps<Props>(), {
        variant: 'primary',
        size: 'md',
        type: 'button',
        disabled: false,
        loading: false,
        plain: false,
        round: false,
        circle: false,
    })
</script>

<style scoped lang="scss">
    // 使用项目 CSS 变量，fallback 为默认值
    $primary-400: var(--color-primary-400, #ffb366);
    $primary-500: var(--color-primary, #ff9e37);
    $primary-600: var(--color-primary-600, #e68a2e);
    $primary-700: var(--color-primary-700, #cc7a29);
    $warning-500: #f97316;
    $warning-600: #ea580c;
    $gray-50: #f9fafb;
    $gray-100: #f3f4f6;
    $gray-200: #e5e7eb;
    $gray-300: #d1d5db;
    $gray-500: #6b7280;
    $gray-700: #374151;
    $gray-900: #111827;
    $red-500: #ef4444;
    $red-600: #dc2626;
    $red-700: #b91c1c;
    $white: #fff;

    .btn-base {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        border: none;
        border-radius: 0.5rem;
        font-weight: 500;
        transition: all 0.2s ease;
        cursor: pointer;

        &:focus-visible {
            outline: none;
            box-shadow:
                0 0 0 2px $white,
                0 0 0 4px $primary-500;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
        }

        &:active:not(:disabled) {
            transform: scale(0.95);
        }
    }

    .btn-loading-icon {
        width: 1rem;
        height: 1rem;
        margin-right: 0.5rem;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }

    // ============ 变体样式 ============
    .btn-primary {
        color: $white;
        background-color: $primary-500;
        box-shadow: 0 10px 20px rgba(255, 158, 55, 0.2);

        &:hover:not(:disabled) {
            background-color: $primary-600;
        }

        &.is-plain {
            border: 2px solid rgba(255, 158, 55, 0.2);
            color: $primary-600;
            background-color: $white;
            box-shadow: none;

            &:hover:not(:disabled) {
                background-color: rgba(255, 158, 55, 0.05);
            }
        }
    }

    .btn-warning {
        color: $white;
        background-color: $warning-500;
        box-shadow: 0 10px 20px rgba(249, 115, 22, 0.2);

        &:hover:not(:disabled) {
            background-color: $warning-600;
        }

        &.is-plain {
            border: 2px solid rgba(249, 115, 22, 0.2);
            color: $warning-600;
            background-color: $white;
            box-shadow: none;

            &:hover:not(:disabled) {
                background-color: rgba(249, 115, 22, 0.05);
            }
        }
    }

    .btn-secondary {
        color: $gray-900;
        background-color: $gray-100;

        &:hover:not(:disabled) {
            background-color: $gray-200;
        }

        &.is-plain {
            border: 2px solid $gray-200;
            color: $gray-700;
            background-color: $white;

            &:hover:not(:disabled) {
                background-color: $gray-50;
            }
        }
    }

    .btn-outline {
        border: 2px solid $gray-300;
        color: $gray-500;
        background-color: $white;

        &:hover:not(:disabled) {
            background-color: $gray-50;
        }
    }

    .btn-ghost {
        color: $gray-700;
        background-color: transparent;

        &:hover:not(:disabled) {
            background-color: $gray-100;
        }
    }

    .btn-danger {
        color: $white;
        background-color: $red-600;

        &:hover:not(:disabled) {
            background-color: $red-700;
        }

        &.is-plain {
            border: 2px solid rgba(220, 38, 38, 0.2);
            color: $red-600;
            background-color: $white;

            &:hover:not(:disabled) {
                background-color: rgba(220, 38, 38, 0.05);
            }
        }
    }

    .btn-link {
        color: $primary-500;
        background-color: transparent;
        text-underline-offset: 4px;

        &:hover:not(:disabled) {
            text-decoration: underline;
        }
    }

    // ============ 尺寸样式 ============
    .btn-size-sm {
        height: 2rem;
        padding: 0 0.75rem;
        font-size: 0.875rem;
    }

    .btn-size-md {
        height: 2.5rem;
        padding: 0 1rem;
        font-size: 0.875rem;
    }

    .btn-size-lg {
        height: 3rem;
        padding: 0 1.5rem;
        font-size: 1rem;
    }

    .btn-size-xl {
        height: auto;
        padding: 1rem 1.5rem;
        font-size: 1rem;
        font-weight: 900;
    }

    .btn-size-icon {
        width: 2.5rem;
        height: 2.5rem;
        padding: 0;
    }

    // ============ 修饰样式 ============
    .is-round {
        border-radius: 1rem;
    }

    .is-circle {
        border-radius: 50%;
    }
</style>
